/** Creative Asset Library (M7) — typed API bindings. */
import { http } from "@/shared/api/http";

export interface AssetCard {
  id: string;
  permanent_asset_id: string;
  workspace_id: string;
  campaign_name: string | null;
  campaign_pack_id: string | null;
  content_type: string;
  category: string;
  status: string;
  version_number: number;
  provider: string;
  model: string;
  brand_brain_version: string | null;
  created_at: string | null;
  updated_at: string | null;
  objective: string | null;
  tags: Array<{ label: string; namespace: string; color: string | null }>;
  excerpt: string | null;
  media_file_ids: string[];
  /** Patch 37.4.21 — lifecycle discriminators for asset-level actions
   *  in the Vault/Library UI. Optional because pre-37.4.21 backend
   *  responses may not include them. */
  payload_maturity?: string | null;
  capability?: string | null;
  /** Patch 37.4.42 — pack-ownership + playbook exportability
   *  discriminators. Backend computes both from a single rule so the
   *  Vault drawer + AI-Studio surfaces read from one source of truth.
   *  Optional so pre-37.4.42 backends do not break the frontend. */
  is_quick_pack?: boolean;
  is_exportable_playbook?: boolean;
  usage: {
    times_scheduled?: number;
    times_published?: number;
    campaigns_used?: number;
    derivative_count?: number;
    regeneration_count?: number;
    export_count?: number;
  };
  fingerprint: { content_hash?: string; generation_hash?: string; similarity_hash?: string };
}

export interface AssetEnvelope {
  permanent_asset_id: string;
  workspace_id: string;
  head: Record<string, any> | null;
  versions: Record<string, any>[];
  brief: Record<string, any> | null;
  campaign_pack: Record<string, any> | null;
  history: Record<string, any>[];
  schedules: Record<string, any>[];
  files: Record<string, any>[];
  tags: Record<string, any>[];
}

export interface LineageNode {
  kind: string;
  id: string | null;
  label: string | null;
  at: string | null;
  detail: Record<string, any> | null;
}

export interface Relationships {
  self: string;
  parent: string | null;
  siblings: string[];
  versions: Array<{ id: string; version_number: number; status: string; created_at: string | null }>;
  derivatives: string[];
  references: Array<{ kind: string; target_permanent_asset_id: string; metadata: Record<string, any> }>;
  translations: string[];
  campaign_pack: { id: string; campaign_name: string | null; objective: string | null; asset_count: number } | null;
}

export interface SmartCollection {
  id: string;
  workspace_id: string | null;
  name: string;
  slug: string;
  description: string | null;
  rule: Record<string, any>;
  icon: string;
  accent: string;
  owner: "system" | "workspace" | "user";
  is_pinned: boolean;
  asset_count: number | null;
}

export interface Playbook {
  id: string;
  permanent_asset_id: string | null;
  campaign_pack_id: string;
  workspace_id: string;
  status: "pending" | "rendering" | "ready" | "failed";
  version_number: number;
  file_id: string | null;
  file_signed_url: string | null;
  strategy_narrative_md: string | null;
  page_count: number;
  renderer: string;
  generated_at: string | null;
}

export interface SearchResponse {
  results: AssetCard[];
  facets: Record<string, Record<string, number>>;
  total: number;
  next_cursor: string | null;
  search_provider: string;
  semantic_available: boolean;
}

export interface LibraryPageResponse {
  results: AssetCard[];
  total: number;
  limit: number;
  offset: number;
  next_offset: number | null;
}

const REFERENCE_KINDS = [
  "derived_from", "inspired_by", "translation_of", "variation_of", "remix_of",
] as const;
export type ReferenceKind = typeof REFERENCE_KINDS[number];
export const REFERENCE_KIND_LIST = REFERENCE_KINDS;

export const listLibrary = (ws: string, limit = 200): Promise<AssetCard[]> =>
  http(`/workspaces/${ws}/library?limit=${limit}`);

export const listLibraryPage = (ws: string, limit = 100, offset = 0): Promise<LibraryPageResponse> =>
  http(`/workspaces/${ws}/library/page?limit=${limit}&offset=${offset}`);

export interface AssetMediaFile {
  id: string;
  mime_type: string | null;
  size_bytes: number | null;
  kind: string | null;
  signed_url: string | null;
}

export interface AssetMediaResponse {
  permanent_asset_id: string;
  files: AssetMediaFile[];
}

export const getAssetMedia = (ws: string, pid: string): Promise<AssetMediaResponse> =>
  http(`/workspaces/${ws}/library/assets/${pid}/media`);

export const getEnvelope = (ws: string, pid: string): Promise<AssetEnvelope> =>
  http(`/workspaces/${ws}/library/assets/${pid}`);

export const getLineage = (ws: string, pid: string): Promise<{ asset_id: string; nodes: LineageNode[] }> =>
  http(`/workspaces/${ws}/library/assets/${pid}/lineage`);

export const getRelationships = (ws: string, pid: string): Promise<Relationships> =>
  http(`/workspaces/${ws}/library/assets/${pid}/relationships`);

export const listCollections = (ws: string, includeCounts = true): Promise<SmartCollection[]> =>
  http(`/workspaces/${ws}/library/collections?include_counts=${includeCounts}`);

export const resolveCollection = (ws: string, slug: string, limit = 200): Promise<AssetCard[]> =>
  http(`/workspaces/${ws}/library/collections/${slug}/assets?limit=${limit}`);

export const resolveCollectionPage = (ws: string, slug: string, limit = 100, offset = 0): Promise<LibraryPageResponse> =>
  http(`/workspaces/${ws}/library/collections/${slug}/page?limit=${limit}&offset=${offset}`);

export const createCollection = (
  ws: string,
  body: { name: string; rule: Record<string, any>; icon?: string; accent?: string; description?: string; is_pinned?: boolean },
): Promise<SmartCollection> =>
  http(`/workspaces/${ws}/library/collections`, { method: "POST", body });

export const deleteCollection = (ws: string, slug: string): Promise<{ status: string }> =>
  http(`/workspaces/${ws}/library/collections/${slug}`, { method: "DELETE" });

export const listTags = (ws: string): Promise<Array<{ label: string; display_label: string; namespace: string; color: string | null; usage_count: number }>> =>
  http(`/workspaces/${ws}/library/tags/all`);

export const assignTags = (ws: string, pid: string, tags: string[]): Promise<{ assigned: number; tags: any[] }> =>
  http(`/workspaces/${ws}/library/tags/assign`, {
    method: "POST",
    body: { permanent_asset_id: pid, tags },
  });

export const addReference = (
  ws: string, pid: string, body: { kind: ReferenceKind; target_permanent_asset_id: string; metadata?: Record<string, any> },
): Promise<any[]> =>
  http(`/workspaces/${ws}/library/assets/${pid}/references`, { method: "POST", body });

export const universalSearch = (ws: string, params: Record<string, string | undefined>): Promise<SearchResponse> => {
  const q = new URLSearchParams(Object.entries(params).filter(([_, v]) => v) as [string, string][]).toString();
  return http(`/workspaces/${ws}/library/search/query${q ? `?${q}` : ""}`);
};

export const exportPlaybook = (ws: string, packId: string, force = false): Promise<Playbook> =>
  http(`/workspaces/${ws}/campaign-packs/${packId}/export-playbook${force ? "?force=true" : ""}`, {
    method: "POST",
  });

export const listPlaybooks = (ws: string, packId: string): Promise<Playbook[]> =>
  http(`/workspaces/${ws}/campaign-packs/${packId}/playbooks`);

export const uploadFile = (ws: string, pid: string, file: File): Promise<any> => {
  const form = new FormData();
  form.append("file", file);
  return http(`/workspaces/${ws}/library/assets/${pid}/files`, { method: "POST", body: form });
};

export const STATUS_TONE: Record<string, string> = {
  draft:         "bg-slate-100 text-slate-800 dark:bg-slate-800/50 dark:text-slate-200",
  needs_review:  "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200",
  approved:      "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200",
  scheduled:     "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200",
  published:     "bg-indigo-100 text-indigo-900 dark:bg-indigo-900/40 dark:text-indigo-200",
  archived:      "bg-zinc-100 text-zinc-500 dark:bg-zinc-900/60 dark:text-zinc-400",
};
